// Sugalarm — exposes the user's currently-active member (cycle 13).
//
// Source of truth is `GET /api/me/active_member` (id + display_name +
// is_self). To surface the relationship_label without adding a backend
// endpoint, the hook also pulls `GET /api/me/members` and resolves the
// matching full `Member` row. The household screen calls `refresh()`
// after a successful `setActive` so the Now screen picks up the change
// without a full page reload.
//
// Cycle 76d: both fetches are now retried with backoff (`retryResult`)
// and re-attempted when the device regains connectivity / is
// foregrounded. Previously this was a single mount fetch with no retry,
// so a transient blip on initial load (the Fly `ord` edge resets seen in
// the field) left the active member unresolved — the Now hero rendered
// `—` / `MEMBER` forever until a manual reload.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as membersApi from "../api/members";
import { retryResult } from "../lib/retryResult";
import type { ActiveMember, Member } from "../lib/types";

export interface UseActiveMember {
  active: ActiveMember | null;
  /** Full Member row matching `active.member_id` (carries relationship_label). */
  fullMember: Member | null;
  members: Member[];
  refresh: () => Promise<void>;
}

/** @param accountKey Cycle 435 (security review): the signed-in ACCOUNT's
 *  id. `/login` is routed inside the mounted shell, so signing in as a
 *  different account only changes the token; without this the previous
 *  account's `member_id` + display name persisted here, the socket's
 *  reseed was tagged with that stale id, and the new account's number was
 *  keyed to, painted, spoken and written to the widgets under the old
 *  account's member. When the key changes the identity is CLEARED at once
 *  and refetched; a token rotation keeps the key. */
/** Cycle 474 (B2): the member-recovery coalescing flag is released after
 *  this long whatever its request does. */
const RECOVER_RELEASE_MS = 15_000;

export function useActiveMember(
  enabled: boolean,
  accountKey: string | null = null,
): UseActiveMember {
  const [active, setActive] = useState<ActiveMember | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const lastAccountRef = useRef<string | null>(accountKey);
  // The account the CURRENT render belongs to, for a refresh started under
  // the previous one (security re-run, LOW): the public `refresh()` has no
  // signal, retries with backoff, and could otherwise land the previous
  // account's member AFTER the clear below.
  const accountKeyRef = useRef<string | null>(accountKey);
  accountKeyRef.current = accountKey;

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (!enabled) {
        setActive(null);
        setMembers([]);
        return;
      }
      const forAccount = accountKeyRef.current;
      const [a, l] = await Promise.all([
        retryResult(() => membersApi.getActive(), { signal }),
        retryResult(() => membersApi.listMine(), { signal }),
      ]);
      if (signal?.aborted) return;
      // Cycle 435: a result for an account that is no longer signed in is
      // dropped — never written under the new one.
      if (accountKeyRef.current !== forAccount) return;
      if (a.ok) setActive(a.data);
      if (l.ok) setMembers(l.data.members);
    },
    [enabled],
  );

  // Mount fetch + recovery listeners. Aborting on cleanup stops any
  // in-flight retry chain when the user signs out / the hook unmounts.
  const recoverInFlight = useRef(false);
  useEffect(() => {
    // Cycle 435: a different account — drop the previous account's member
    // identity BEFORE the refetch, so nothing of it can label a number, a
    // spoken line or a widget seed in the gap.
    if (lastAccountRef.current !== accountKey) {
      lastAccountRef.current = accountKey;
      setActive(null);
      setMembers([]);
    }
    const ctrl = new AbortController();
    void refresh(ctrl.signal);

    if (!enabled) return () => ctrl.abort();

    // Cycle 76d: re-attempt when the browser regains connectivity or the
    // tab is foregrounded — a transient mount failure can no longer
    // strand the member identity until a manual reload. Coalesced so
    // overlapping `online` / `visibilitychange` events don't stack.
    const onRecover = () => {
      if (document.visibilityState === "hidden") return;
      if (recoverInFlight.current) return;
      recoverInFlight.current = true;
      // ⚠ Cycle 474 (B2): the release must not depend on the fetch
      // settling. iOS can freeze a request mid-flight for minutes, and this
      // flag used to stay held until then — every later wake's member
      // recovery was dropped and a null active member showed No Data until
      // a restart. A watchdog releases it after RECOVER_RELEASE_MS whatever
      // the request does; the `finally` still releases it on an answer.
      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        window.clearTimeout(watchdog);
        recoverInFlight.current = false;
      };
      const watchdog = window.setTimeout(release, RECOVER_RELEASE_MS);
      void refresh(ctrl.signal).finally(release);
    };
    window.addEventListener("online", onRecover);
    document.addEventListener("visibilitychange", onRecover);
    return () => {
      ctrl.abort();
      window.removeEventListener("online", onRecover);
      document.removeEventListener("visibilitychange", onRecover);
    };
  }, [enabled, refresh, accountKey]);

  const fullMember = useMemo<Member | null>(() => {
    if (!active) return null;
    return members.find((m) => m.id === active.member_id) ?? null;
  }, [active, members]);

  // Public `refresh` keeps its no-arg signature for callers (e.g. the
  // household screen after `setActive`).
  const publicRefresh = useCallback(() => refresh(), [refresh]);

  return { active, fullMember, members, refresh: publicRefresh };
}
